/**
 * `$localize` runtime polyfill for module-federation-loaded Angular MFEs.
 *
 * Angular components compiled with i18n reference the GLOBAL `$localize`
 * tagged-template function. A standalone Angular app installs it via
 * `@angular/localize/init` in its polyfills bundle. MFEs loaded as
 * module-federation remotes into this React shell never run their own
 * polyfills, so `$localize` is undefined on the host window and their template
 * `consts` throw `ReferenceError: $localize is not defined`, which aborts the
 * component render and leaves the MFE partially/incorrectly laid out
 * (e.g. the STF "Create Support Ticket" form).
 *
 * This installs the *pass-through* runtime `$localize` (single-locale /
 * no-translation mode) ported verbatim from `@angular/localize` v20
 * (`fesm2022/localize2.mjs`): it strips the leading metadata / placeholder
 * block from each message part and interleaves the static parts with the
 * substitution expressions, exactly like the real polyfill when
 * `$localize.translate` is unset. We only need the pass-through path because
 * the MFEs run a single (en) locale with no runtime translation table.
 */

const BLOCK_MARKER = ':';

/** Find the end of a "marked block" indicated by the first non-escaped colon. */
function findEndOfBlock(cooked: string, raw: string): number {
  for (let cookedIndex = 1, rawIndex = 1; cookedIndex < cooked.length; cookedIndex++, rawIndex++) {
    if (raw[rawIndex] === '\\') {
      rawIndex++;
    } else if (cooked[cookedIndex] === BLOCK_MARKER) {
      return cookedIndex;
    }
  }
  throw new Error(`Unterminated $localize metadata block in "${raw}".`);
}

/** Strip a delimited metadata/placeholder block from the start of a message part. */
function stripBlock(messagePart: string, rawMessagePart: string): string {
  return rawMessagePart.charAt(0) === BLOCK_MARKER
    ? messagePart.substring(findEndOfBlock(messagePart, rawMessagePart) + 1)
    : messagePart;
}

type LocalizeFn = {
  (messageParts: TemplateStringsArray, ...expressions: readonly unknown[]): string;
  translate?: (
    messageParts: TemplateStringsArray,
    expressions: readonly unknown[],
  ) => [TemplateStringsArray, readonly unknown[]];
};

const $localize: LocalizeFn = function (
  messageParts: TemplateStringsArray,
  ...expressions: readonly unknown[]
): string {
  if ($localize.translate) {
    const translation = $localize.translate(messageParts, expressions);
    messageParts = translation[0];
    expressions = translation[1];
  }
  let message = stripBlock(messageParts[0], messageParts.raw[0]);
  for (let i = 1; i < messageParts.length; i++) {
    message += String(expressions[i - 1]) + stripBlock(messageParts[i], messageParts.raw[i]);
  }
  return message;
};

/**
 * Install the pass-through `$localize` on the global scope, once. No-op if a
 * real `$localize` (e.g. an MFE's own `@angular/localize/init`) is already
 * present. Must run BEFORE any Angular MFE bundle evaluates.
 */
export function installLocalizePolyfill(): void {
  const g = globalThis as unknown as { $localize?: LocalizeFn };
  if (typeof g.$localize === 'function') return;
  g.$localize = $localize;
}
